import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../utils/firebase";

export interface Taxonomies {
  styles: string[];
  industries: string[];
  formats: string[];
}

export type TaxonomyKey = keyof Taxonomies;

export const TAXONOMY_LABELS: Record<TaxonomyKey, string> = {
  industries: "Industries",
  formats: "Formats",
  styles: "Styles",
};

// Lists previously hard-coded in the admin forms. Used until an admin saves
// the lists from the CMS, and as a fallback if Firestore is unreachable.
export const DEFAULT_TAXONOMIES: Taxonomies = {
  styles: ["2D", "3D", "Flat Design", "Gradient", "Motion & Video", "Collage", "Fake 3D"],
  industries: [
    "SaaS",
    "Finance",
    "Cryptos",
    "AI",
    "Health",
    "Gaming",
    "French tech",
    "Y Combinator",
    "Environment",
    "Bank",
    "Agency",
    "Creative independant",
    "Food",
    "Freelance",
    "CRM",
    "Rebranding",
    "Tutorial",
    "Use Case",
  ],
  formats: [
    "Ad",
    "Explainer",
    "Feature launch",
    "Update",
    "Product demo",
    "Showreel",
    "Product presentation",
  ],
};

const TAXONOMY_DOC = doc(db, "settings", "taxonomies");

let cache: Promise<Taxonomies> | null = null;

export function loadTaxonomies(force = false): Promise<Taxonomies> {
  if (!cache || force) {
    cache = getDoc(TAXONOMY_DOC)
      .then((snap) => {
        const data = snap.exists() ? (snap.data() as Partial<Taxonomies>) : {};
        return {
          styles: data.styles?.length ? data.styles : DEFAULT_TAXONOMIES.styles,
          industries: data.industries?.length ? data.industries : DEFAULT_TAXONOMIES.industries,
          formats: data.formats?.length ? data.formats : DEFAULT_TAXONOMIES.formats,
        };
      })
      .catch((err) => {
        console.error("Error loading taxonomies:", err);
        cache = null;
        return DEFAULT_TAXONOMIES;
      });
  }
  return cache;
}

export async function saveTaxonomies(next: Taxonomies): Promise<void> {
  const clean = (list: string[]) =>
    Array.from(new Set(list.map((v) => v.trim()).filter(Boolean)));
  const payload: Taxonomies = {
    styles: clean(next.styles),
    industries: clean(next.industries),
    formats: clean(next.formats),
  };
  await setDoc(TAXONOMY_DOC, { ...payload, modifiedAt: Timestamp.now() });
  cache = Promise.resolve(payload);
}
